import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, HttpError } from '../api/client';
import { AppHeader } from '../components/AppHeader';
import Toast, { type ToastMsg } from '../components/Toast';
import { useStoreSlice } from '../store/storeRegistry';
import { useAuth } from '../store/auth';
import { summarizeFilter } from '../lib/filterSummary';
import type { DesignConfig } from '../api/types';

function errDetail(err: unknown): string {
  return err instanceof HttpError ? err.detail : 'unknown error';
}

export default function ConfigList() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { selectedStoreId } = useStoreSlice();
  const me = useAuth((s) => s.me);
  const isAdmin = me?.role === 'admin';
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const { data: configs, isLoading, isError } = useQuery({
    queryKey: ['configs', selectedStoreId],
    queryFn: () => api.listConfigs(selectedStoreId),
    // No accessible store (0-store client, or store not yet resolved): don't
    // fetch (would hit the legacy widforss route → 403). Show the empty state.
    enabled: !!selectedStoreId,
  });

  const del = useMutation({
    mutationFn: (name: string) => api.deleteConfig(name, selectedStoreId),
    onSuccess: (_d, name) => {
      qc.invalidateQueries({ queryKey: ['configs', selectedStoreId] });
      setToast({ kind: 'success', msg: `Deleted "${name}".` });
      setTimeout(() => setToast(null), 2200);
    },
    onError: (err, name) =>
      setToast({ kind: 'error', msg: `Couldn't delete "${name}": ${errDetail(err)}` }),
  });
  const dup = useMutation({
    mutationFn: (name: string) => api.duplicateConfig(name, selectedStoreId),
    onSuccess: (cfg) => {
      qc.invalidateQueries({ queryKey: ['configs', selectedStoreId] });
      nav(`/edit/${encodeURIComponent(cfg.name)}`);
    },
    onError: (err, name) =>
      setToast({ kind: 'error', msg: `Couldn't duplicate "${name}": ${errDetail(err)}` }),
  });

  const sorted = [...(configs ?? [])].sort(
    (a, b) => b.priority - a.priority || a.name.localeCompare(b.name),
  );
  const defaultCfg = sorted.find((c) => c.name === '_default');
  const others = sorted.filter((c) => c.name !== '_default');

  return (
    <div className="min-h-screen bg-ff-ivory relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />

      <AppHeader active="configs" />

      <main className="relative mx-auto max-w-[1240px] px-5 pb-24 pt-10 animate-rise sm:px-8 sm:pt-16">
        {/* Title block */}
        <div className="grid items-end gap-8 border-b border-ff-line pb-10 lg:grid-cols-[1.35fr_1fr] lg:gap-12 lg:pb-14">
          <div>
            <p className="ff-kicker mb-4">Design workspace</p>
            <h1 className="ff-page-title text-[clamp(3.25rem,7vw,5.5rem)] leading-[0.92]">
              Your catalog
              <span className="block text-ff-orange">designs.</span>
            </h1>
          </div>
          <div className="flex flex-col gap-5 lg:items-end">
            <p className="max-w-md text-base leading-relaxed text-ff-mute lg:text-right">
              The renderer picks the highest-priority config whose filter matches each
              product. The default catches anything else.
            </p>
            <button
              onClick={() => dup.mutate('_default')}
              disabled={dup.isPending}
              className="ff-btn-primary w-full sm:w-auto"
              data-testid="new-from-default"
            >
              {dup.isPending ? 'Drafting…' : '+ New from default'}
            </button>
          </div>
        </div>

        {!selectedStoreId && <NoStoreState isAdmin={isAdmin} />}

        {selectedStoreId && isLoading && <Skeleton />}
        {selectedStoreId && isError && (
          <p className="mt-12 text-sm text-ff-danger">
            Couldn't load configs. Check that the backend is reachable and you're signed in.
          </p>
        )}

        {selectedStoreId && !isLoading && !isError && (
          <div className="mt-12 space-y-12">
            {/* Default */}
            {defaultCfg && (
              <section>
                <SectionLabel>The default</SectionLabel>
                <ConfigRow
                  config={defaultCfg}
                  onDelete={() => {}}
                  onDuplicate={() => dup.mutate(defaultCfg.name)}
                />
              </section>
            )}

            {/* Named */}
            <section>
              <SectionLabel>
                Named configs
                <span className="ml-2 text-ff-mute">
                  ({others.length})
                </span>
              </SectionLabel>
              {others.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="divide-y divide-ff-sand/60 border-t border-b border-ff-sand/60">
                  {others.map((c) => (
                    <ConfigRow
                      key={c.name}
                      config={c}
                      onDelete={() => del.mutate(c.name)}
                      onDuplicate={() => dup.mutate(c.name)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

/** Two-step inline delete (W-C [UX-1]): the first click arms the button, the
 * second deletes. Arms disarm on blur or after 4s — a stray click can no
 * longer destroy a live campaign config. */
function ArmedDeleteButton({ name, onDelete }: { name: string; onDelete: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      onClick={() => {
        if (armed) {
          setArmed(false);
          onDelete();
        } else {
          setArmed(true);
        }
      }}
      onBlur={() => setArmed(false)}
      className={
        armed
          ? 'ff-btn-danger px-4 py-2.5 ring-2 ring-ff-danger/40'
          : 'ff-btn-danger px-4 py-2.5'
      }
      data-testid={`delete-${name}`}
      aria-label={armed ? `Confirm deleting ${name}` : `Delete ${name}`}
    >
      {armed ? 'Really delete?' : 'Delete'}
    </button>
  );
}

function ConfigRow({
  config,
  onDelete,
  onDuplicate,
}: {
  config: DesignConfig;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const isDefault = config.name === '_default';

  const filterText = summarizeFilter(config.filter);

  return (
    <article
      className={
        isDefault
          ? 'group relative rounded-2xl border border-ff-line bg-white shadow-soft p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-5 transition-shadow hover:shadow-hover'
          : 'group flex flex-col sm:flex-row sm:items-center justify-between gap-5 py-7 px-2 transition-colors hover:bg-ff-mint/20'
      }
      data-testid={`config-row-${config.name}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="font-display text-2xl font-bold leading-tight tracking-tightish text-ff-ink truncate">
            {config.name}
          </h3>
          {isDefault && (
            <span className="ff-chip bg-ff-ink text-white">Default</span>
          )}
          <span className="ff-chip bg-ff-paper text-ff-mute">
            <span className="font-data normal-case tracking-normal text-ff-ink/70">P{config.priority}</span>
          </span>
        </div>
        <p className="font-data text-sm text-ff-mute">
          <span className="text-ff-mute/70 mr-1">filter:</span>
          <span className="text-ff-ink/80">{filterText}</span>
        </p>
      </div>

      <div className="flex w-full shrink-0 flex-wrap items-center gap-1 sm:w-auto sm:flex-nowrap">
        <Link
          to={`/edit/${encodeURIComponent(config.name)}`}
          className="ff-btn-ghost flex-1 sm:flex-none"
          data-testid={`edit-${config.name}`}
        >
          Edit
          <span aria-hidden="true">→</span>
        </Link>
        <button
          onClick={onDuplicate}
          className="ff-btn text-ff-mute hover:text-ff-forest hover:bg-ff-mint/60 px-4 py-2.5"
          title="Duplicate"
        >
          Duplicate
        </button>
        {!isDefault && <ArmedDeleteButton name={config.name} onDelete={onDelete} />}
      </div>
    </article>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.12em] text-ff-mute">
      <span className="h-px w-8 bg-ff-orange" />
      {children}
    </h2>
  );
}

function NoStoreState({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="mt-16 rounded-2xl border border-dashed border-ff-line bg-white/70 px-6 py-14 text-center sm:px-10">
      <p className="font-display text-2xl font-bold text-ff-ink">No stores yet.</p>
      <p className="mt-2 text-sm text-ff-mute max-w-md mx-auto">
        {isAdmin
          ? 'Create a store from the selector above to get started.'
          : 'Your account has no stores assigned yet. Ask your admin to assign one to you.'}
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-ff-line bg-white/70 px-6 py-14 text-center sm:px-10">
      <p className="font-display text-2xl font-bold text-ff-ink">Nothing else, yet.</p>
      <p className="mt-2 text-sm text-ff-mute max-w-md mx-auto">
        Click <span className="font-medium text-ff-ink">+ New from default</span> above to
        spin up a variant — campaign-specific colours, badges, or a feed-field
        filter.
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="mt-12 space-y-6">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-24 rounded-xl bg-gradient-to-r from-ff-paper via-white to-ff-paper animate-pulse"
        />
      ))}
    </div>
  );
}
